import { Badge, ButtonBase } from '@mui/material';
import { NavLink } from 'react-router';

import { useOutstandingCount } from '@/features/outstanding';

import { bottomNavigation } from './navigation';

/**
 * The phone's primary navigation: a floating dock rather than an edge-to-edge
 * bar. Detaching it from the screen edge keeps it clear of the home indicator
 * and of browser chrome that slides in and out, and the pill shape reads as
 * one control with two positions — which is what a two-destination nav is.
 *
 * The surface is translucent and blurred, matching the header, so the two
 * pieces of chrome read as one family. It floats over a near-white canvas, so
 * the hairline border is load-bearing rather than decorative — without it the
 * dock's edge disappears against the page.
 *
 * Links, not buttons: these are destinations, so they get anchors and all the
 * behaviour a director expects from one (long-press, open in a new tab).
 */
export const BottomNav = () => {
  const outstanding = useOutstandingCount();

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-10 flex justify-center px-4 pb-safe-3 md:hidden">
      <nav
        aria-label="Primary"
        className="pointer-events-auto flex items-center gap-1 rounded-full border border-divider bg-surface/85 p-1.5 shadow-card backdrop-blur-md"
      >
        {bottomNavigation.map(({ label, to, icon: Icon, end }) => (
          <ButtonBase
            key={to}
            component={NavLink}
            to={to}
            end={end ?? false}
            focusRipple
            // The label is rendered for every tab and only revealed on the
            // active one, so the accessible name never depends on selection.
            // Animating `grid-template-columns` from 0fr to 1fr reveals it
            // without measuring anything in JS.
            className="group h-11 gap-2 rounded-full px-4 text-content-muted transition-colors duration-200 aria-[current=page]:bg-primary aria-[current=page]:text-on-primary motion-reduce:transition-none"
          >
            {/* Only the dashboard carries the count. `badgeContent` of 0
                renders nothing, so a caught-up director sees a plain icon. */}
            {to === '/' ? (
              <Badge
                badgeContent={outstanding}
                color="error"
                max={99}
                // On the active tab the badge sits against the filled pill, so
                // it needs the pill's own colour as its cutout, not the bar's.
                className="[&_.MuiBadge-badge]:ring-2 [&_.MuiBadge-badge]:ring-surface group-aria-[current=page]:[&_.MuiBadge-badge]:ring-primary"
              >
                <Icon fontSize="small" />
              </Badge>
            ) : (
              <Icon fontSize="small" />
            )}

            <span className="grid grid-cols-[0fr] transition-[grid-template-columns] duration-200 ease-out group-aria-[current=page]:grid-cols-[1fr] motion-reduce:transition-none">
              <span className="overflow-hidden whitespace-nowrap">
                <span className="block text-sm font-semibold">{label}</span>
              </span>
            </span>
          </ButtonBase>
        ))}
      </nav>
    </div>
  );
};
